<template>
  <div class="relative z-[200]" :class="{ 'hidden': !isOpen}">
    <Overlay :visible="isOpen" @click="closeSidebar" />
    <Drawer
      ref="invoiceSidebarRef"
      class="w-full sm:w-[600px] p-7 pt-0 flex flex-col"
      :is-open="isOpen"
      @click:close="closeSidebar"
    >
      <template #header>
        <div class="title">{{ t('account.orders.addInvoiceInformation') }}</div>
      </template>

      <div class="flex flex-col flex-1">
        <div class="invoice-sidebar__info-banner">
          <IconInfo class="text-teal-500" />
          <p>{{ t('account.orders.invoiceEmailInfo') }}</p>
        </div>

        <form
          class="invoice-sidebar__form flex flex-col flex-1"
          @submit.prevent="handleSubmit"
        >
          <div class="invoice-sidebar__field">
            <label class="invoice-sidebar__label">
              {{ t('account.orders.firstName') }}
            </label>
            <input
              v-model="form.firstName"
              type="text"
              class="invoice-sidebar__input"
            />
          </div>

          <div class="invoice-sidebar__field">
            <label class="invoice-sidebar__label">
              {{ t('account.orders.lastName') }}
            </label>
            <input
              v-model="form.lastName"
              type="text"
              class="invoice-sidebar__input"
            />
          </div>

          <div class="invoice-sidebar__field">
            <label
              class="invoice-sidebar__label invoice-sidebar__label--required"
            >
              {{ t('account.orders.street') }}
            </label>
            <input
              v-model="form.street"
              type="text"
              class="invoice-sidebar__input"
              :class="{ 'invoice-sidebar__input--error': errors.street }"
              required
            />
            <span v-if="errors.street" class="invoice-sidebar__error">
              {{ errors.street }}
            </span>
          </div>

          <div class="invoice-sidebar__field">
            <label
              class="invoice-sidebar__label invoice-sidebar__label--required"
            >
              {{ t('account.orders.houseNumber') }}
            </label>
            <input
              v-model="form.houseNumber"
              type="text"
              class="invoice-sidebar__input"
              :class="{ 'invoice-sidebar__input--error': errors.houseNumber }"
              required
            />
            <span v-if="errors.houseNumber" class="invoice-sidebar__error">
              {{ errors.houseNumber }}
            </span>
          </div>

          <div class="invoice-sidebar__field">
            <label
              class="invoice-sidebar__label invoice-sidebar__label--required"
            >
              {{ t('account.orders.postalCode') }}
            </label>
            <input
              v-model="form.postalCode"
              type="text"
              class="invoice-sidebar__input"
              :class="{ 'invoice-sidebar__input--error': errors.postalCode }"
              required
            />
            <span v-if="errors.postalCode" class="invoice-sidebar__error">
              {{ errors.postalCode }}
            </span>
          </div>

          <div class="invoice-sidebar__field">
            <label
              class="invoice-sidebar__label invoice-sidebar__label--required"
            >
              {{ t('account.orders.city') }}
            </label>
            <input
              v-model="form.city"
              type="text"
              class="invoice-sidebar__input"
              :class="{ 'invoice-sidebar__input--error': errors.city }"
              required
            />
            <span v-if="errors.city" class="invoice-sidebar__error">
              {{ errors.city }}
            </span>
          </div>

          <div class="invoice-sidebar__field">
            <label class="invoice-sidebar__label">
              {{ t('account.orders.companyName') }}
            </label>
            <input
              v-model="form.companyName"
              type="text"
              class="invoice-sidebar__input"
            />
          </div>

          <div class="invoice-sidebar__field">
            <label class="invoice-sidebar__label">
              {{ t('account.orders.vatId') }}
            </label>
            <input
              v-model="form.vatId"
              type="text"
              class="invoice-sidebar__input"
            />
          </div>

          <div class="invoice-sidebar__field">
            <label class="invoice-sidebar__label">
              {{ t('account.orders.po') }}
            </label>
            <!-- Backend expects PO number as custom_notes -->
            <input
              v-model="form.customNotes"
              type="text"
              class="invoice-sidebar__input"
            />
          </div>

          <div class="invoice-sidebar__field">
            <label class="invoice-sidebar__label">
              {{ t('account.orders.country') }}
            </label>
            <select v-model="form.country" class="invoice-sidebar__select">
              <option value="DE">{{ t('account.orders.germany') }}</option>
              <option value="AT">{{ t('account.orders.austria') }}</option>
            </select>
          </div>

          <div class="invoice-sidebar__checkbox">
            <input
              id="agree-to-email"
              v-model="form.agreeToEmail"
              type="checkbox"
              class="invoice-sidebar__checkbox-input"
              required
            />
            <label for="agree-to-email" class="invoice-sidebar__checkbox-label">
              {{ t('account.orders.agreeToEmailInvoice') }}
            </label>
          </div>

          <button
            type="submit"
            class="invoice-sidebar__submit"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? t('general.loading') : t('account.orders.send') }}
          </button>
        </form>
      </div>
    </Drawer>
  </div>
</template>

<script setup lang="ts">
import { toast, type ToastOptions } from 'vue3-toastify'
import IconInfo from '~/assets/icons/info-circle-fill.svg?component'

const invoiceSidebarRef = ref(null)

const { t } = useI18n()
const { getAccessToken } = useAuthTokens()
const { user } = useAccount()

interface InvoiceForm {
  firstName: string
  lastName: string
  street: string
  houseNumber: string
  postalCode: string
  city: string
  companyName: string
  vatId: string
  country: string
  agreeToEmail: boolean
  customNotes: string
}

interface FormErrors {
  street?: string
  houseNumber?: string
  postalCode?: string
  city?: string
}

const props = defineProps<{
  isOpen: boolean
  orderId: string
  orderItemId: string
}>()

const emit = defineEmits<{
  close: []
  'invoice-success': [data?: { has_customer_invoice: boolean }]
}>()

const form = ref<InvoiceForm>({
  firstName: (user.value as any)?.data?.first_name || '',
  lastName: (user.value as any)?.data?.last_name || '',
  street: '',
  houseNumber: '',
  postalCode: '',
  city: '',
  companyName: (user.value as any)?.data?.customer?.data?.company_name || '',
  vatId: '',
  country: 'DE',
  agreeToEmail: false,
  customNotes: '',
})

const errors = ref<FormErrors>({})
const isSubmitting = ref(false)

const closeSidebar = () => {
  emit('close')
}

const validateForm = (): boolean => {
  errors.value = {}
  let isValid = true

  if (!form.value.street.trim()) {
    errors.value.street = t('validation.required')
    isValid = false
  }

  if (!form.value.houseNumber.trim()) {
    errors.value.houseNumber = t('validation.required')
    isValid = false
  }

  if (!form.value.postalCode.trim()) {
    errors.value.postalCode = t('validation.required')
    isValid = false
  }

  if (!form.value.city.trim()) {
    errors.value.city = t('validation.required')
    isValid = false
  }

  if (!form.value.agreeToEmail) {
    isValid = false
  }

  return isValid
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  isSubmitting.value = true

  try {
    const token = await getAccessToken()

    // Map form data to API parameters
    const requestData = {
      street: form.value.street || null,
      street_number: form.value.houseNumber || null,
      zipcode: form.value.postalCode || null,
      locality: form.value.city || null,
      country: form.value.country || null,
      first_name: form.value.firstName || null,
      last_name: form.value.lastName || null,
      company_name: form.value.companyName || null,
      vat: form.value.vatId || null,
      invoice_due_date: null,
      custom_notes: form.value.customNotes || null,
      custom_notes2: null,
    }

    const response = await kftApiRequest(
      `/v1/store/orders/${props.orderId}/invoice-data`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      }
    )

    if (response) {
      // Check if has_customer_invoice is true and emit the updated status
      if (response.data?.has_customer_invoice) {
        emit('invoice-success', { has_customer_invoice: true })
      } else {
        emit('invoice-success')
      }
      closeSidebar()
    }
  } catch (error) {
    toast(t('general.error'), {
      autoClose: 2000,
      type: 'error',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
  } finally {
    isSubmitting.value = false
  }
}

// Reset form when sidebar opens
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      form.value = {
        firstName: (user.value as any)?.data?.first_name || '',
        lastName: (user.value as any)?.data?.last_name || '',
        street: '',
        houseNumber: '',
        postalCode: '',
        city: '',
        companyName:
          (user.value as any)?.data?.customer?.data?.company_name || '',
        vatId: '',
        country: 'DE',
        agreeToEmail: false,
        customNotes: '',
      }
      errors.value = {}
    }
  }
)

defineExpose({ closeSidebar })
</script>

<style>
body.prevent-scroll {
  overflow: hidden;
}
</style>

<style lang="postcss" scoped>
.title {
  @apply h-14 flex items-center text-lg font-medium text-[#666971] mb-[-1rem];
}

.invoice-sidebar__info-banner {
  @apply flex items-start gap-3 p-4 rounded-lg mb-6 bg-teal-50;
}

.invoice-sidebar__info-banner :deep(svg) {
  @apply w-5 h-5  mt-0.5 flex-shrink-0;
}

.invoice-sidebar__info-banner p {
  @apply text-sm;
}

.invoice-sidebar__form {
  @apply space-y-4;
}

.invoice-sidebar__form > div:not(:last-child) {
  @apply space-y-4;
}

.invoice-sidebar__field {
  @apply space-y-2;
}

.invoice-sidebar__label {
  @apply block text-sm font-medium text-gray-700;
}

.invoice-sidebar__label--required::after {
  content: '*';
  @apply ml-1;
}

.invoice-sidebar__input,
.invoice-sidebar__select {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors;
}

.invoice-sidebar__input--error {
  @apply p-0;
}

.invoice-sidebar__error {
  @apply text-sm;
}

.invoice-sidebar__checkbox {
  @apply flex items-start gap-3 pt-4;
}

.invoice-sidebar__checkbox-input {
  @apply mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 accent-teal-600;
}

.invoice-sidebar__checkbox-label {
  @apply text-sm text-gray-700 cursor-pointer !mt-0;
}

.invoice-sidebar__submit {
  @apply w-full bg-teal-900 text-white py-3 px-4 rounded-lg font-medium hover:bg-teal-700 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed;
  @apply sticky -bottom-0;
}

.invoice-sidebar__submit:disabled {
  @apply bg-gray-400 hover:bg-gray-400;
}
</style>
